/**
 * CollapsibleGlassCard Component
 * A GlassCard with a clickable header that toggles content visibility
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { GlassCard, GlassCardHeader, GlassCardTitle, GlassCardContent } from './GlassCard';
import { cn } from '@/lib/utils';

interface CollapsibleGlassCardProps {
  title: React.ReactNode;
  /** Extra elements rendered at the right side of the header (next to chevron) */
  headerActions?: React.ReactNode;
  /** Whether the card starts expanded */
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function CollapsibleGlassCard({
  title,
  headerActions,
  defaultOpen = true,
  children,
  className,
}: CollapsibleGlassCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <GlassCard className={className}>
      <GlassCardHeader
        className="flex flex-row items-center justify-between cursor-pointer select-none"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <GlassCardTitle>{title}</GlassCardTitle>
        <div className="flex items-center gap-2">
          {headerActions && (
            <div onClick={(e) => e.stopPropagation()}>
              {headerActions}
            </div>
          )}
          <ChevronDown
            className={cn(
              'w-5 h-5 text-gray-400 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </GlassCardHeader>
      <div
        className={cn(
          'grid transition-all duration-300 ease-in-out',
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <GlassCardContent>{children}</GlassCardContent>
        </div>
      </div>
    </GlassCard>
  );
}
